/**
 * useDraftCampaign — return-or-create the student's active campaign draft.
 *
 * Backed by /api/campaigns/mine/draft. Called on Dashboard mount so all
 * three asset uploaders can pass `campaign_id` on their upload calls
 * (which mirrors the FK back onto the campaign row automatically).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const formatDollars = (cents) => {
  const n = (cents || 0) / 100;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: n % 1 ? 2 : 0 });
};

export const useDraftCampaign = ({ auto = true } = {}) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(auto);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await client.get("/campaigns/mine/draft");
      setDraft(res.data);
      return res.data;
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        setError(err?.response?.data?.detail || "Could not load your campaign draft.");
      }
      setDraft(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  const patch = useCallback(async (body) => {
    if (!draft) return null;
    setSaving(true);
    setError("");
    try {
      const res = await client.patch(`/campaigns/${draft.id}`, body);
      setDraft(res.data);
      return res.data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail[0]?.msg : (detail || "Save failed."));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const submitForReview = useCallback(async () => {
    return await patch({ status: "pending_review" });
  }, [patch]);

  const discardPendingChanges = useCallback(async () => {
    if (!draft) return null;
    const res = await client.delete(`/campaigns/${draft.id}/pending-changes`);
    setDraft(res.data);
    return res.data;
  }, [draft]);

  const requestExtension = useCallback(async (reason) => {
    if (!draft) return null;
    const res = await client.post(`/campaigns/${draft.id}/extension`, { reason });
    setDraft(res.data);
    return res.data;
  }, [draft]);

  const startNewCampaign = useCallback(async () => {
    const res = await client.post("/campaigns", {});
    setDraft(res.data);
    return res.data;
  }, []);

  return { draft, loading, saving, error, refresh, patch, submitForReview, discardPendingChanges, requestExtension, startNewCampaign };
};


/**
 * useMyLiveCampaign — dashboard-only. Returns the student's most recent
 * LIVE campaign (or null). Used by the Approval Celebration banner.
 */
export const useMyLiveCampaign = ({ auto = true } = {}) => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(auto);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/campaigns/mine/live");
      // Endpoint returns 200 with null when the student has no live campaign
      setLive(res.data && res.data.id ? res.data : null);
    } catch {
      setLive(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  return { live, loading, refresh };
};
